import useIdentity from "@/features/ui/hooks/use-identity";
import { useCallback, useEffect, useMemo } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useListAllSheetTabs, {
  ListAllSheetTabsPathParameter,
  ListAllSheetTabsRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-sheet-tabs";
import { useSetRecoilState } from "recoil";
import { inflatedTabsFamily } from "@/features/line-sheet-sets/helpers/sheet-state";
import {
  isProductFiltered,
  SheetTabFilterQuery,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import useGetListOrderSheetTabsWithRevision from "@/features/line-sheet-sets/hooks/use-list-all-sheet-tabs-revision";
import { CompanyType } from "@/features/types";
import {
  GetOrderSheetRevisionPathParameter,
  GetOrderSheetRevisionRequest,
} from "@/features/line-sheet-sets/hooks/use-get-order-sheet-revision";

interface UseFetchAndSetOrderSheetTabsParameter {
  data?: {
    buyerCompanyId: number;
    orderSheetSetId: number;
    orderSheetId: number;
    revisionNumber: number;
    latestRevisionNumber: number;
    lineSheetSetId: number;
    lineSheetId: number;
    tabIndexes: number[];
    orderSheetSetStatus: string;
  };
  query?: SheetTabFilterQuery;
}

export default function useFetchAndSetOrderSheetTabs({
  data,
  query,
}: UseFetchAndSetOrderSheetTabsParameter) {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const { error: showError } = useAppToasts();

  const useRevisionFetch = !!(
    data &&
    data.latestRevisionNumber > -1 &&
    data.revisionNumber > -1 &&
    data.latestRevisionNumber !== data.revisionNumber
  );

  const listAllSheetTabsKey = useMemo(():
    | {
        parameter: ListAllSheetTabsPathParameter;
        request: ListAllSheetTabsRequest;
      }
    | undefined => {
    if (data && company && !useRevisionFetch) {
      return {
        parameter: {
          by: company.type,
          companyId: company.id,
          orderSheetSetId: data.orderSheetSetId,
          orderSheetId: data.orderSheetId,
        },
        request: {
          lineSheetSetId: data.lineSheetSetId,
          lineSheetId: data.lineSheetId,
          sheetTabIndexes: data.tabIndexes,
        },
      };
    }
  }, [company, data, useRevisionFetch]);

  const {
    isLoading: isListAllSheetTabsLoading,
    isValidating: isListAllSheetTabsValidating,
    data: listAllSheetTabsData,
    error: listAllSheetTabsError,
    mutate: mutateListAllSheetTabs,
  } = useListAllSheetTabs(
    listAllSheetTabsKey?.parameter,
    listAllSheetTabsKey?.request,
    {
      revalidateOnFocus: false,
    }
  );

  const listAllSheetTabsRevisionKey = useMemo((): {
    parameter: GetOrderSheetRevisionPathParameter;
    request: GetOrderSheetRevisionRequest;
  } => {
    if (data && company && useRevisionFetch) {
      return {
        parameter: {
          by: company.type,
          companyId: company.id,
          orderSheetSetId: data.orderSheetSetId,
          orderSheetId: data.orderSheetId,
          revisionNumber: data.revisionNumber,
        },
        request: {
          lineSheetSetId: data.lineSheetSetId,
          lineSheetId: data.lineSheetId,
          sheetTabIndexes: data.tabIndexes,
        },
      };
    }
    return {
      parameter: {
        by: "BUYER" as CompanyType,
        companyId: -1,
        orderSheetSetId: -1,
        orderSheetId: -1,
        revisionNumber: -1,
      },
      request: {
        lineSheetSetId: -1,
        lineSheetId: -1,
        sheetTabIndexes: [],
      },
    };
  }, [company, data, useRevisionFetch]);

  const {
    isLoading: isGetListAllSheetTabsWithRevisionLoading,
    isValidating: isGetListAllSheetTabsWithRevisionValidating,
    data: getListAllSheetTabsWithRevisionData,
    error: getListAllSheetTabsWithRevisionError,
    mutate: mutateGetListAllSheetTabsWithRevision,
  } = useGetListOrderSheetTabsWithRevision(
    listAllSheetTabsRevisionKey.parameter,
    listAllSheetTabsRevisionKey.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    showError(listAllSheetTabsError);
  }, [showError, listAllSheetTabsError]);

  const sheetKey = useMemo(() => {
    return {
      orderSheetSetId: data?.orderSheetSetId || -1,
      orderSheetId: data?.orderSheetId || -1,
      revisionNumber: data?.revisionNumber || -1,
    };
  }, [data?.orderSheetId, data?.orderSheetSetId, data?.revisionNumber]);

  const setInflatedTabsFamily = useSetRecoilState(inflatedTabsFamily(sheetKey));
  const { filterInflatedProducts, resetInflatedSheet } = useInflatedTabs();
  //query가 변경되면 반복 실행되어버림..
  //tabIndex, brandName__eq같은 filter가 변경되어도..
  useEffect(() => {
    const activeData = useRevisionFetch
      ? getListAllSheetTabsWithRevisionData
      : listAllSheetTabsData;
    const activeIsLoading = useRevisionFetch
      ? isGetListAllSheetTabsWithRevisionLoading ||
        isGetListAllSheetTabsWithRevisionValidating
      : isListAllSheetTabsLoading || isListAllSheetTabsValidating;

    if (!activeIsLoading && activeData && sheetKey) {
      setInflatedTabsFamily((prev) => {
        const sheetTabs = activeData.sheetTabs;

        // 상태(편집 모드)가 바뀌면(예: 주문 → 확정/오더협의중) prev를 재사용하지 않고
        // 새로 받아온 데이터로 다시 빌드한다. 그래야 confirmed_* 등 확정 필드가
        // 초기화된 최신 데이터가 그리드에 반영된다. (재사용하면 stale 데이터가 남아
        // 새로고침 전까지 확정수량/컨디션 편집이 잘못된 필드로 동작함)
        const incomingStatus = data?.orderSheetSetStatus || "";
        const statusChanged =
          !!prev && prev.orderSheetSetStatus !== incomingStatus;
        const reusePrev = !!prev && !statusChanged;

        // Save 후 다시 불러오는 상황이면, isDirty를 false로 초기화해주어야 함.
        // mutateListAllSheetTabs가 save 후에만 호출되고 있으므로, prev?.tabs를 사용하는 경우에도 isDirty가 false로 초기화되어야 함.
        let tabs =
          (reusePrev
            ? prev!.tabs.map((tab) => {
                return {
                  ...tab,
                  isDirty: false,
                };
              })
            : undefined) ||
          sheetTabs.map((tab) => {
            return {
              ...tab,
              products: tab.products.map((product) => {
                return {
                  ...product,
                  priceAdjustedBy: {
                    ...product.priceAdjustedBy,
                    value: product.priceAdjustedBy.value,
                  },
                  // isFiltered: isProductFiltered(product, query),
                };
              }),
              isDirty: false,
            };
          });
        //prev.tabs에 없는 상품이 있으면 추가
        if (reusePrev && prev) {
          const prevProductIds = new Set(
            prev.tabs.flatMap((tab) => tab.products.map((product) => product.id))
          );
          const newProducts = sheetTabs.flatMap((tab) =>
            tab.products.filter((product) => !prevProductIds.has(product.id))
          );

          if (newProducts.length > 0) {
            tabs = tabs.map((tab) => {
              return {
                ...tab,
                products: [
                  ...tab.products.map((product) => {
                    return {
                      ...product,
                      isFiltered: true,
                      isDirty: false,
                  }
                  }),
                  ...newProducts.map((product) => {
                    return {
                      ...product,
                      isFiltered: true,
                      isDirty: false,
                    }
                  }),
                ],
              };
            });
          }
        }

        return {
          key: sheetKey,
          isBuyer: company?.type === "BUYER",
          orderSheetSetStatus: data?.orderSheetSetStatus || "",
          tabs: tabs.map((tab) => {
            return {
              ...tab,
              products: tab.products.map((product) => {
                return {
                  ...product,
                  isFiltered: isProductFiltered(product, query),
                  isDirty: false,
                };
              }),
            };
          }),
          // isDirty: false,
        };
      });
    }
  }, [
    isListAllSheetTabsValidating,
    isListAllSheetTabsLoading,
    isGetListAllSheetTabsWithRevisionLoading,
    isGetListAllSheetTabsWithRevisionValidating,
    listAllSheetTabsData,
    getListAllSheetTabsWithRevisionData,
    setInflatedTabsFamily,
    query,
    sheetKey,
    data?.latestRevisionNumber,
    company?.type,
    data,
    useRevisionFetch,
  ]);

  useEffect(() => {
    return () => {
      resetInflatedSheet(sheetKey);
    };
  }, [resetInflatedSheet, sheetKey]);

  const setInflatedTabsClean = useCallback(() => {
    if (data) {
      setInflatedTabsFamily((prev) => {
        if (prev) {
          return {
            ...prev,
            tabs: prev.tabs.map((tab) => {
              return {
                ...tab,
                isDirty: false,
              };
            }),
          };
        } else {
          return prev;
        }
      });
    }
  }, [setInflatedTabsFamily, data]);

  return {
    mutateListAllSheetTabs,
    mutateListAllSheetTabsRevision: mutateGetListAllSheetTabsWithRevision,
    setInflatedTabsClean,
  };
}
